// src/prototype/utils/mapStrategicExecutionPlan.ts
// Maps /api/v2/strategic-execution-plan response → SEP UI view models

import type { SEPFinalist } from '../components/sep/SEPReportView';
import type {
  SEPInitiativePortfolioItem,
  SEPPortfolioBucketInitiative,
  SEPResourceInitiative,
  SEPRoadmapInitiative,
  SEPSelectedInitiative,
  StrategicExecutionPlanResponse,
  type StrategicExecutionPlanSections,
} from '../services/strategicExecutionPlanService';

export function toMillions(dollars: number | null | undefined): number {
  if (dollars == null || !Number.isFinite(dollars)) return 0;
  return dollars / 1_000_000;
}

/** Coerce nullable API numbers to a finite value for display/math. */
export function safeNumber(value: number | null | undefined): number {
  return Number.isFinite(value) ? value : 0;
}

export function formatRoiMillions(low: number, high: number): string {
  return `$${low.toFixed(1)}–$${high.toFixed(1)}M`;
}

/** API may send validation_score as 0–1 or as an already-scaled percentage. */
export function normalizeValidationScore(score?: number | null): number {
  if (score == null || !Number.isFinite(score)) return 0;
  if (score <= 1) return Math.round(score * 100);
  return Math.round(score);
}

function mapEffort(
  quickWin?: boolean,
  implementationEffort?: string,
  effortTier?: string
): 'Low' | 'Medium' | 'High' {
  if (quickWin) return 'Low';
  const raw = (implementationEffort || effortTier || 'Medium').toLowerCase();
  if (raw === 'low') return 'Low';
  if (raw === 'high') return 'High';
  return 'Medium';
}

/** Waterfall row from financial_summary — same source Section 3 uses. */
export type ScenarioWaterfallSource = {
  conservative: number;
  base: number;
  aggressive: number;
  paybackMonths?: number;
  paybackMonthsConservative?: number | null;
  paybackMonthsBase?: number | null;
  paybackMonthsAggressive?: number | null;
};

function buildScenarios(
  item: SEPInitiativePortfolioItem | SEPSelectedInitiative,
  waterfall?: ScenarioWaterfallSource | null
) {
  // Prefer financial_summary.waterfall_table so §3 and §4 stay aligned.
  if (waterfall) {
    const basePayback = waterfall.paybackMonthsBase ?? waterfall.paybackMonths ?? 2;
    const conservativePayback =
      waterfall.paybackMonthsConservative ?? waterfall.paybackMonths ?? basePayback;
    const aggressivePayback =
      waterfall.paybackMonthsAggressive ?? waterfall.paybackMonths ?? basePayback;
    const conservative = safeNumber(waterfall.conservative);
    const base = safeNumber(waterfall.base);
    const aggressive = safeNumber(waterfall.aggressive);
    return {
      conservative: {
        roiLow: conservative,
        roiHigh: conservative,
        paybackMonths: conservativePayback,
      },
      base: {
        roiLow: base,
        roiHigh: base,
        paybackMonths: basePayback,
      },
      aggressive: {
        roiLow: aggressive,
        roiHigh: aggressive,
        paybackMonths: aggressivePayback,
      },
    };
  }

  const projections =
    ('scenario_projections' in item && item.scenario_projections) ||
    ('scenarios' in item && item.scenarios) ||
    {};

  const conservative = projections.conservative;
  const base = projections.base;
  const aggressive = projections.aggressive;

  // ROI values from the API are already in millions — do not call toMillions()
  const roiEstimate = 'roiEstimate' in item ? item.roiEstimate : item.roi_estimate;
  const fallbackLow = safeNumber(roiEstimate?.low);
  const fallbackHigh = safeNumber(roiEstimate?.high);
  const itemPayback =
    'payback_months' in item && typeof item.payback_months === 'number'
      ? item.payback_months
      : undefined;

  // Prefer ROI-model scenario points; fall back to overall cons–aggr band (no invented multipliers).
  return {
    conservative: {
      roiLow: safeNumber(conservative?.roiHigh ?? fallbackLow),
      roiHigh: safeNumber(conservative?.roiHigh ?? fallbackLow),
      paybackMonths: conservative?.payback_months ?? itemPayback ?? 3,
    },
    base: {
      roiLow: safeNumber(base?.roiHigh ?? fallbackLow),
      roiHigh: safeNumber(base?.roiHigh ?? fallbackHigh),
      paybackMonths: base?.payback_months ?? itemPayback ?? 2,
    },
    aggressive: {
      roiLow: safeNumber(aggressive?.roiHigh ?? fallbackHigh),
      roiHigh: safeNumber(aggressive?.roiHigh ?? fallbackHigh),
      paybackMonths: aggressive?.payback_months ?? itemPayback ?? 2,
    },
  };
}

function normalizeNameKey(name: string | undefined | null): string {
  return (name ?? '').trim().toLowerCase();
}

function mapInitiativeToFinalist(
  item: SEPInitiativePortfolioItem | SEPSelectedInitiative,
  portfolioItem?: SEPInitiativePortfolioItem,
  waterfall?: ScenarioWaterfallSource | null
): SEPFinalist {
  const roiSource = portfolioItem || item;
  const roiEstimate =
    'roiEstimate' in roiSource ? roiSource.roiEstimate : roiSource.roi_estimate;

  const implEffort =
    ('implementationEffort' in item && typeof item.implementationEffort === 'string' && item.implementationEffort) ||
    ('implementation_effort' in item && typeof item.implementation_effort === 'string' && item.implementation_effort) ||
    ('complexity' in item && typeof item.complexity === 'string' && item.complexity) ||
    undefined;

  const effortTier =
    ('effort_tier' in item && item.effort_tier) ||
    ('effortTier' in item && (item as SEPInitiativePortfolioItem).effort_tier) ||
    undefined;

  const quickWin = 'quick_win' in item ? item.quick_win : undefined;

  const scenarios = buildScenarios(portfolioItem || item, waterfall);

  // ROI Range = Conservative–Aggressive so it matches scenario cards (§3 waterfall when available)
  const rangeLow = safeNumber(
    waterfall?.conservative ?? scenarios.conservative.roiHigh ?? roiEstimate?.low
  );
  const rangeHigh = safeNumber(
    waterfall?.aggressive ?? scenarios.aggressive.roiHigh ?? roiEstimate?.high
  );

  return {
    id: item.use_case_id != null ? String(item.use_case_id) : normalizeNameKey(item.name) || `rank-${item.rank ?? 0}`,
    rank: item.rank ?? 0,
    title: item.name,
    category: item.category_name,
    implementationEffort: mapEffort(quickWin, implEffort, effortTier),
    timeToValue:
      ('timeToValue' in item && item.timeToValue) ||
      ('time_to_value' in item && item.time_to_value) ||
      ('timeline' in item && item.timeline) ||
      ('duration' in item && item.duration) ||
      '3-6 months',
    // ROI values from the API are already in millions
    roiEstimate: {
      low: rangeLow,
      high: rangeHigh,
      unit: 'M',
    },
    validationScore: normalizeValidationScore(
      ('validationScore' in item && typeof item.validationScore === 'number' && item.validationScore) ||
        ('validation_score' in item && typeof item.validation_score === 'number' && item.validation_score) ||
        undefined
    ),
    executionRationale:
      ('executionRationale' in item && item.executionRationale) ||
      `${item.name} is selected based on strategic alignment and implementation feasibility.`,
    scenarios,
  };
}

export interface MappedPortfolioOverviewInitiative {
  id: string;
  title: string;
  category: string;
  annualBenefitMillions: number;
  roiEstimate: { low: number; high: number; unit: string };
  timeToValue: string;
}

export function mapPortfolioBucketInitiative(
  init: SEPPortfolioBucketInitiative
): MappedPortfolioOverviewInitiative {
  return {
    id: String(init.use_case_id),
    title: init.name,
    category: init.category_name,
    // annual_benefit_base is raw dollars — convert to millions
    annualBenefitMillions: toMillions(init.annual_benefit_base),
    // roiEstimate values are already in millions
    roiEstimate: {
      low: safeNumber(init.roiEstimate?.low),
      high: safeNumber(init.roiEstimate?.high),
      unit: 'M',
    },
    timeToValue: init.timeToValue || init.timeline || '',
  };
}

export interface MappedInitiativePortfolioRow {
  id: string;
  index: number;
  rank: number;
  title: string;
  category: string;
  complexity: string;
  timeToValue: string;
  roiEstimate: { low: number; high: number; unit: string };
  validationScore: number;
  valueBand: string;
  executionRationale: string;
  scenarios: SEPFinalist['scenarios'];
}

export function mapInitiativePortfolioItem(
  item: SEPInitiativePortfolioItem,
  waterfall?: ScenarioWaterfallSource | null
): MappedInitiativePortfolioRow {
  const mapped = mapInitiativeToFinalist(item, undefined, waterfall);
  return {
    id: mapped.id,
    index: item.index,
    rank: item.rank ?? 0,
    title: mapped.title,
    category: mapped.category,
    complexity: item.complexity || item.implementationEffort || 'Medium',
    timeToValue: mapped.timeToValue,
    roiEstimate: mapped.roiEstimate,
    validationScore: mapped.validationScore,
    valueBand: item.value_band || '',
    executionRationale: mapped.executionRationale,
    scenarios: mapped.scenarios,
  };
}

export interface MappedExecutionRoadmapPhase {
  name: string;
  startMonth: number;
  endMonth: number;
}

export interface MappedExecutionRoadmapMilestone {
  label: string;
  monthsFromStart: number;
  valueNote?: string;
}

export interface MappedExecutionRoadmapInitiative {
  id: string;
  title: string;
  startMonth: number;
  endMonth: number;
  phases: MappedExecutionRoadmapPhase[];
  milestones: MappedExecutionRoadmapMilestone[];
}

export function mapExecutionRoadmapInitiative(
  item: SEPRoadmapInitiative
): MappedExecutionRoadmapInitiative {
  return {
    id: String(item.use_case_id),
    title: item.name,
    startMonth: item.start_month,
    endMonth: item.end_month,
    phases: (item.phases ?? []).map(phase => ({
      name: phase.name,
      startMonth: phase.start_month,
      endMonth: phase.end_month,
    })),
    milestones: (item.milestones ?? []).map(milestone => ({
      label: milestone.label,
      monthsFromStart: milestone.months_from_start,
      valueNote: milestone.value_note,
    })),
  };
}

export interface MappedResourcePlanRole {
  role: string;
  team: string;
  fte: number;
  scope: string;
}

export interface MappedResourcePlanInitiative {
  id: string;
  index: number;
  title: string;
  fteTotal: number;
  roles: MappedResourcePlanRole[];
}

export function mapResourcePlanInitiative(
  item: SEPResourceInitiative,
  fallbackIndex?: number
): MappedResourcePlanInitiative {
  return {
    id: String(item.use_case_id),
    index: item.index ?? fallbackIndex ?? 1,
    title: item.name,
    fteTotal: item.fte_total,
    roles: (item.roles ?? []).map(role => ({
      role: role.role,
      team: role.team,
      fte: role.fte,
      scope: role.scope,
    })),
  };
}

export interface MappedRoiComparisonRow {
  id: string;
  name: string;
  roiLow: number;
  roiHigh: number;
  barWidthPercent: number;
}

export interface MappedWaterfallRow {
  id: string;
  name: string;
  conservative: number;
  base: number;
  aggressive: number;
  paybackMonths?: number;
  paybackMonthsConservative?: number | null;
  paybackMonthsBase?: number | null;
  paybackMonthsAggressive?: number | null;
}

export interface MappedFinancialSummary {
  portfolioRoiLow: number;
  portfolioRoiHigh: number;
  initiativesSelectedCount: number;
  roiComparison: MappedRoiComparisonRow[];
  scenarioTotals: { conservative: number; base: number; aggressive: number };
  waterfallTable: MappedWaterfallRow[];
}

export function mapFinancialSummary(
  financialSummary: StrategicExecutionPlanSections['financial_summary']
): MappedFinancialSummary | null {
  if (!financialSummary) return null;

  const range = financialSummary.portfolio_value_range;

  return {
    portfolioRoiLow: safeNumber(range.low_millions ?? toMillions(range.low)),
    portfolioRoiHigh: safeNumber(range.high_millions ?? toMillions(range.high)),
    initiativesSelectedCount: financialSummary.initiatives_selected_count ?? 0,
    // roi_estimate values are already in millions; use_case_id may be absent in newer API
    roiComparison: (financialSummary.roi_comparison ?? []).map(row => ({
      id: String(row.use_case_id ?? row.name),
      name: row.name,
      roiLow: safeNumber(row.roi_estimate?.low),
      roiHigh: safeNumber(row.roi_estimate?.high),
      barWidthPercent: safeNumber(row.bar_width_percent),
    })),
    // scenario totals are already in millions
    scenarioTotals: {
      conservative: safeNumber(financialSummary.scenarios?.conservative?.roiHigh),
      base: safeNumber(financialSummary.scenarios?.base?.roiHigh),
      aggressive: safeNumber(financialSummary.scenarios?.aggressive?.roiHigh),
    },
    // waterfall values are already in millions; use_case_id may be absent in newer API
    waterfallTable: (financialSummary.waterfall_table ?? []).map(row => ({
      id: String(row.use_case_id ?? row.name),
      name: row.name,
      conservative: safeNumber(row.conservative),
      base: safeNumber(row.base),
      aggressive: safeNumber(row.aggressive),
      paybackMonths: row.payback_months,
      paybackMonthsConservative: row.payback_months_conservative ?? row.payback_months,
      paybackMonthsBase: row.payback_months_base ?? row.payback_months,
      paybackMonthsAggressive: row.payback_months_aggressive ?? row.payback_months,
    })),
  };
}

export interface MappedApprovalStep {
  step: number;
  title: string;
  description: string;
  responsibleGroup: string;
  isFinal: boolean;
}

export interface MappedOversightCadence {
  frequency: string;
  meetingType: string;
  participants: string;
}

export interface MappedGovernanceFramework {
  approvalWorkflow: MappedApprovalStep[];
  oversightCadence: MappedOversightCadence[];
}

export function mapGovernanceFramework(
  governance: StrategicExecutionPlanSections['governance_framework']
): MappedGovernanceFramework | null {
  if (!governance) return null;

  return {
    approvalWorkflow: (governance.approval_workflow ?? []).map(step => ({
      step: step.step,
      title: step.title,
      description: step.description,
      responsibleGroup: step.responsible_group ?? '',
      isFinal: Boolean(step.is_final),
    })),
    oversightCadence: (governance.oversight_cadence ?? []).map(item => ({
      frequency: item.frequency,
      meetingType: item.meeting_type,
      participants: item.participants,
    })),
  };
}

export interface MappedSharedDependency {
  id: string;
  title: string;
  priority: string;
  relatedInitiatives: string[];
}

export interface MappedRiskItem {
  id: string;
  name: string;
  likelihood: string;
  impact: string;
  mitigation: string;
  owner: string;
}

export interface MappedRiskByInitiative {
  id: string;
  initiativeName: string;
  risks: MappedRiskItem[];
}

export interface MappedRisksAndDependencies {
  sharedDependencies: MappedSharedDependency[];
  riskByInitiative: MappedRiskByInitiative[];
}

export function mapRisksAndDependencies(
  risksAndDependencies: StrategicExecutionPlanSections['risks_and_dependencies']
): MappedRisksAndDependencies | null {
  if (!risksAndDependencies) return null;

  return {
    sharedDependencies: (risksAndDependencies.shared_portfolio_dependencies ?? []).map(dep => ({
      id: dep.id,
      title: dep.title,
      priority: dep.priority ?? '',
      relatedInitiatives: dep.related_initiatives ?? [],
    })),
    riskByInitiative: (risksAndDependencies.risk_register?.by_initiative ?? []).map(group => ({
      id: String(group.use_case_id),
      initiativeName: group.initiative_name,
      risks: (group.risks ?? []).map(risk => ({
        id: risk.risk_id ?? `${group.use_case_id}-${risk.name}`,
        name: risk.name,
        likelihood: risk.likelihood,
        impact: risk.impact,
        mitigation: risk.mitigation,
        owner: risk.owner ?? '',
      })),
    })),
  };
}

export interface MappedPilotInitiative {
  id: string;
  name: string;
}

export interface MappedRecommendedNextStep {
  step: number;
  title: string;
  description: string;
  timeline: string;
  pilotInitiatives: MappedPilotInitiative[];
}

export interface MappedApprovalChecklist {
  portfolioRoiLow: number;
  portfolioRoiHigh: number;
  portfolioRoiDisplay: string;
  executionHorizon: string;
  initiativesSelectedCount: number;
  quickWinInitiative: MappedPilotInitiative | null;
  advisorySummary: string[];
  recommendedNextSteps: MappedRecommendedNextStep[];
}

export function mapApprovalChecklist(
  approvalChecklist: StrategicExecutionPlanSections['approval_checklist']
): MappedApprovalChecklist | null {
  if (!approvalChecklist) return null;

  const portfolioRoi = approvalChecklist.portfolio_roi;

  return {
    portfolioRoiLow:
      portfolioRoi?.roi_low_millions ?? toMillions(portfolioRoi?.roi_low),
    portfolioRoiHigh:
      portfolioRoi?.roi_high_millions ?? toMillions(portfolioRoi?.roi_high),
    portfolioRoiDisplay: portfolioRoi?.display_range ?? '',
    executionHorizon: approvalChecklist.execution_horizon,
    initiativesSelectedCount: approvalChecklist.initiatives_selected_count,
    quickWinInitiative: approvalChecklist.quick_win_initiative
      ? {
          id: String(approvalChecklist.quick_win_initiative.use_case_id),
          name: approvalChecklist.quick_win_initiative.name,
        }
      : null,
    advisorySummary: approvalChecklist.advisory_summary ?? [],
    recommendedNextSteps: (approvalChecklist.recommended_next_steps ?? []).map(step => ({
      step: step.step,
      title: step.title,
      description: step.description,
      timeline: step.timeline,
      pilotInitiatives: (step.pilot_initiatives ?? []).map(init => ({
        id: String(init.use_case_id),
        name: init.name,
      })),
    })),
  };
}

export interface MappedStrategicExecutionPlan {
  clientName: string;
  finalists: SEPFinalist[];
  /** From executive_summary.initiatives_selected_count */
  initiativesSelectedCount: number;
  /** Mapped from executive_summary.selected_initiatives (Component 1 source of truth) */
  executiveSummaryInitiatives: SEPFinalist[];
  /** Mapped from initiative_portfolio.initiatives (Component 4 source of truth) */
  initiativePortfolioItems: MappedInitiativePortfolioRow[];
  /** Mapped from execution_roadmap.initiatives (Component 5 source of truth) */
  executionRoadmapInitiatives: MappedExecutionRoadmapInitiative[];
  /** Portfolio delivery span (months) shared by §1 Execution Horizon + §5 timeline */
  executionHorizonMonths: number;
  /** Mapped from resource_requirements.initiatives (Component 6 source of truth) */
  resourcePlanInitiatives: MappedResourcePlanInitiative[];
  /** Mapped from financial_summary (Component 7 source of truth) */
  financialSummary: MappedFinancialSummary | null;
  /** Mapped from governance_framework (Component 8 source of truth) */
  governanceFramework: MappedGovernanceFramework | null;
  /** Mapped from risks_and_dependencies (Component 9 source of truth) */
  risksAndDependencies: MappedRisksAndDependencies | null;
  /** Mapped from approval_checklist (Component 10 source of truth) */
  approvalChecklist: MappedApprovalChecklist | null;
  portfolioRoiLow: number;
  portfolioRoiHigh: number;
  executionHorizon: string;
  quickWinCount: number;
  hasData: boolean;
}

export function mapStrategicExecutionPlan(
  response: StrategicExecutionPlanResponse
): MappedStrategicExecutionPlan {
  const { sections } = response;
  const execSummary = sections.executive_summary;
  const initiativePortfolio = sections.initiative_portfolio?.initiatives ?? [];

  const portfolioById = new Map<string, SEPInitiativePortfolioItem>();
  const portfolioByName = new Map<string, SEPInitiativePortfolioItem>();
  for (const item of initiativePortfolio) {
    if (item.use_case_id != null) portfolioById.set(String(item.use_case_id), item);
    portfolioByName.set(normalizeNameKey(item.name), item);
  }

  // Index waterfall by name/id so §4 scenarios match §3 Portfolio Financial Impact
  const waterfallRows = sections.financial_summary?.waterfall_table ?? [];
  const waterfallByKey = new Map<string, ScenarioWaterfallSource>();
  for (const row of waterfallRows) {
    const source: ScenarioWaterfallSource = {
      conservative: safeNumber(row.conservative),
      base: safeNumber(row.base),
      aggressive: safeNumber(row.aggressive),
      paybackMonths: row.payback_months,
      paybackMonthsConservative: row.payback_months_conservative ?? row.payback_months,
      paybackMonthsBase: row.payback_months_base ?? row.payback_months,
      paybackMonthsAggressive: row.payback_months_aggressive ?? row.payback_months,
    };
    if (row.use_case_id != null) waterfallByKey.set(String(row.use_case_id), source);
    waterfallByKey.set(normalizeNameKey(row.name), source);
  }

  const resolveWaterfall = (
    item: SEPInitiativePortfolioItem | SEPSelectedInitiative
  ): ScenarioWaterfallSource | null => {
    const byId = item.use_case_id != null ? waterfallByKey.get(String(item.use_case_id)) : undefined;
    return byId ?? waterfallByKey.get(normalizeNameKey(item.name)) ?? null;
  };

  const resolvePortfolioItem = (
    item: SEPInitiativePortfolioItem | SEPSelectedInitiative
  ): SEPInitiativePortfolioItem | undefined => {
    if ('index' in item) return item as SEPInitiativePortfolioItem;
    if (item.use_case_id != null) {
      const byId = portfolioById.get(String(item.use_case_id));
      if (byId) return byId;
    }
    return portfolioByName.get(normalizeNameKey(item.name));
  };

  const selectedInitiatives = execSummary?.selected_initiatives ?? [];

  // Canonical portfolio list: initiative_portfolio is the complete set for §4.
  // selected_initiatives is often incomplete (e.g. count=4 but only 3 cards) — prefer the longer list.
  const canonicalInitiatives: Array<SEPInitiativePortfolioItem | SEPSelectedInitiative> =
    initiativePortfolio.length >= selectedInitiatives.length && initiativePortfolio.length > 0
      ? initiativePortfolio
      : selectedInitiatives.length > 0
        ? selectedInitiatives
        : initiativePortfolio;

  const sortByRank = <T extends { rank?: number | null }>(items: T[]): T[] =>
    [...items].sort(
      (a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER)
    );

  const sortedCanonical = sortByRank(canonicalInitiatives);

  const executiveSummaryInitiatives = sortedCanonical.map(item => {
    const portfolioItem = resolvePortfolioItem(item);
    return mapInitiativeToFinalist(item, portfolioItem, resolveWaterfall(portfolioItem || item));
  });

  const sourceInitiatives =
    initiativePortfolio.length > 0 ? sortByRank(initiativePortfolio) : sortedCanonical;

  const finalists = sourceInitiatives.map(item => {
    const portfolioItem = resolvePortfolioItem(item);
    return mapInitiativeToFinalist(item, portfolioItem, resolveWaterfall(portfolioItem || item));
  });

  const clientName =
    execSummary?.organization_name ||
    sections.approval_checklist?.organization_name ||
    sections.portfolio_overview?.organization_name ||
    'Organization';

  const portfolioRoiLow =
    execSummary?.portfolio_roi?.roi_low_millions ??
    toMillions(sections.financial_summary?.portfolio_value_range?.low);

  const portfolioRoiHigh =
    execSummary?.portfolio_roi?.roi_high_millions ??
    toMillions(sections.financial_summary?.portfolio_value_range?.high);

  // Count must match the cards we actually render (not a stale API count).
  const initiativesSelectedCount = Math.max(
    executiveSummaryInitiatives.length,
    finalists.length,
    sortedCanonical.length,
  );

  const initiativePortfolioItems = (
    initiativePortfolio.length > 0 ? sortByRank(initiativePortfolio) : sortedCanonical
  ).map((item, i) => {
    const portfolioItem =
      ('index' in item
        ? (item as SEPInitiativePortfolioItem)
        : portfolioById.get(String(item.use_case_id))) ||
      ({
        index: i + 1,
        use_case_id: item.use_case_id,
        rank: item.rank ?? i + 1,
        name: item.name,
        category_name: item.category_name,
        roiEstimate:
          'roiEstimate' in item
            ? item.roiEstimate
            : 'roi_estimate' in item
              ? item.roi_estimate
              : { low: 0, high: 0 },
      } as SEPInitiativePortfolioItem);

    return {
      ...mapInitiativePortfolioItem(portfolioItem, resolveWaterfall(portfolioItem)),
      index: i + 1,
    };
  });

  const executionRoadmap = sections.execution_roadmap;
  const executionRoadmapInitiatives = (executionRoadmap?.initiatives ?? []).map(
    mapExecutionRoadmapInitiative
  );

  // Single portfolio delivery horizon for §1 KPI + §5 timeline/copy.
  // Prefer the actual roadmap span (initiative ends / milestones) over stale
  // executive_summary strings like "6–9 Months" that describe time-to-value.
  const declaredHorizonMonths = executionRoadmap?.horizon_months ?? 12;
  const roadmapSpanMonths = executionRoadmapInitiatives.reduce((max, init) => {
    const milestoneMax = init.milestones.reduce(
      (m, milestone) => Math.max(m, milestone.monthsFromStart),
      0
    );
    return Math.max(max, init.endMonth, milestoneMax);
  }, 0);
  const executionHorizonMonths = Math.max(declaredHorizonMonths, roadmapSpanMonths, 1);
  const executionHorizon = `${executionHorizonMonths} Months`;

  // Single Quick Win count for §1 + §2. Prefer effort_tier (same source as §2 cards),
  // then Low-effort finalists — not executive_summary.quick_win_count when it is 0/stale.
  const effortTier = sections.execution_approach?.effort_tier;
  const lowEffortCount = finalists.filter(f => f.implementationEffort === 'Low').length;
  const quickWinCount =
    effortTier?.quick_win ??
    sections.execution_roadmap?.sequencing_insights?.quick_win_count ??
    (lowEffortCount > 0 ? lowEffortCount : execSummary?.quick_win_count ?? 0);

  const resourcePlanInitiatives = (sections.resource_requirements?.initiatives ?? []).map(
    (item, i) => mapResourcePlanInitiative(item, i + 1)
  );

  const financialSummary = mapFinancialSummary(sections.financial_summary);
  const governanceFramework = mapGovernanceFramework(sections.governance_framework);
  const risksAndDependencies = mapRisksAndDependencies(sections.risks_and_dependencies);
  const approvalChecklist = mapApprovalChecklist(sections.approval_checklist);

  const hasData =
    executiveSummaryInitiatives.length > 0 || finalists.length > 0;

  return {
    clientName,
    finalists,
    initiativesSelectedCount,
    executiveSummaryInitiatives,
    initiativePortfolioItems,
    executionRoadmapInitiatives,
    executionHorizonMonths,
    resourcePlanInitiatives,
    financialSummary,
    governanceFramework,
    risksAndDependencies,
    approvalChecklist,
    portfolioRoiLow,
    portfolioRoiHigh,
    executionHorizon,
    quickWinCount,
    hasData,
  };
}
